import React from 'react';
import { View, Text } from 'react-native';
import { ColorFunction } from '@styles/colors/types';
import { font as initialFont } from '@styles/fonts';
import { formatMachineEventDataForDisplay } from '@v2/pages/common/components/NCFileBatchEvents/helpers';
import { FormattedDataItem } from '@v2/pages/common/components/NCFileBatchEvents/types';
import { RawMachineEventDataDisplayProps } from '@v2/pages/common/components/NCFileBatchEvents/components/RawMachineEventDataDisplay';

export interface UseManageRawMachineEventDataDisplayParams extends RawMachineEventDataDisplayProps {
  C: ColorFunction;
}

export const useManageRawMachineEventDataDisplay = ({
  data,
  scale,
  font = initialFont,
  C,
}: UseManageRawMachineEventDataDisplayParams) => {
  const formattedData = formatMachineEventDataForDisplay(data);

  const renderItem = (item: FormattedDataItem, index: number): React.ReactNode => {
    const indentWidth = scale!(item.indent * 20);

    if (item.type === 'primitive') {
      // For primitive values, show "• key: value;" or just "• value;" if key is empty
      if (item.key) {
        return (
          <View
            key={`${item.key}-${item.indent}-${index}`}
            style={{
              marginLeft: indentWidth,
              marginVertical: scale!(2),
              flexDirection: 'row',
              flexWrap: 'wrap',
            }}
          >
            <Text style={[font.Body, { color: C('Label/Light/Secondary') }]}>{item.key}: </Text>
            <Text style={[font.Headline, { color: C('Label/Light/Secondary') }]}>{item.value as string};</Text>
          </View>
        );
      } else {
        return (
          <View
            key={`${item.key}-${item.indent}-${index}`}
            style={{
              marginLeft: indentWidth,
              marginVertical: scale!(2),
              flexDirection: 'row',
              flexWrap: 'wrap',
            }}
          >
            <Text style={[font.Headline, { color: C('Label/Light/Secondary') }]}>{item.value as string};</Text>
          </View>
        );
      }
    }

    if (item.type === 'array' || item.type === 'object') {
      const childItems = item.value as FormattedDataItem[];

      return (
        <View key={`${item.key}-${item.indent}-${index}`} style={{ marginLeft: indentWidth }}>
          <Text
            style={[
              font.Body,
              {
                marginVertical: scale!(2),
                color: C('Label/Light/Primary'),
              },
            ]}
          >
            {item.key}:
          </Text>
          {childItems.map((child, childIndex) => renderItem(child, childIndex))}
        </View>
      );
    }

    return null;
  };

  return {
    formattedData,
    renderItem: (item: FormattedDataItem, index: number) => renderItem(item, index),
  };
};
